import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { ChevronDown, Settings } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore, useServerStore } from '@/store'
import { voiceChannelParticipants, users } from '@/mocks'
import { Badge, Avatar, TierBadge } from '../common'
import { ChannelItem } from '../app/channel/ChannelItem'
import { CategoryGroup } from '../app/channel/CategoryGroup'
import { ServerHeaderDropdown } from '../app/server/ServerHeaderDropdown'
import { FuelSupportBanner } from '../app/server/FuelSupportBanner'
import { StatusPicker } from '../app/settings/StatusPicker'

const Sidebar = styled(View, {
  width: 240,
  backgroundColor: '$sidebar',
  flexDirection: 'column',
  flexShrink: 0,
  borderRightWidth: 1,
  borderRightColor: '$borderDefault',
})

const ServerHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$surfaceRaised',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const ServerName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
})

const UserSection = styled(View, {
  height: 52,
  paddingHorizontal: 12,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  borderTopWidth: 1,
  borderTopColor: '$borderDefault',
  backgroundColor: '$color2',
})

const Username = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 13,
  color: '$surfaceForeground',
  flex: 1,
})

const UserTag = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$color8',
})

const SettingsButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

export function ChannelSidebar() {
  const { activeServerId, activeChannelId, setActiveChannel, joinVoiceChannel, openProfileModal, serverHeaderDropdownOpen, setServerHeaderDropdownOpen, navigateToUserSettings, streamerMode } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const reorderChannels = useServerStore((s) => s.reorderChannels)
  const moveChannelToCategory = useServerStore((s) => s.moveChannelToCategory)
  const theme = useTheme()

  // Channel drag-and-drop state
  const [channelDragOverId, setChannelDragOverId] = useState<string | null>(null)
  const [channelDragPosition, setChannelDragPosition] = useState<'above' | 'below' | null>(null)
  const [isDraggingChannel, setIsDraggingChannel] = useState<string | null>(null)
  const [draggingCategoryId, setDraggingCategoryId] = useState<string | null>(null)
  const [statusPickerOpen, setStatusPickerOpen] = useState(false)

  const handleChannelDragStart = (e: React.DragEvent, channelId: string, categoryId: string) => {
    e.dataTransfer.setData('text/plain', channelId)
    e.dataTransfer.setData('application/x-category', categoryId)
    e.dataTransfer.effectAllowed = 'move'
    setIsDraggingChannel(channelId)
    setDraggingCategoryId(categoryId)
  }

  const handleChannelDragOver = (e: React.DragEvent, channelId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    setChannelDragOverId(channelId)
    setChannelDragPosition(e.clientY < midY ? 'above' : 'below')
  }

  const handleChannelDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    const draggedId = e.dataTransfer.getData('text/plain')
    if (!draggedId || draggedId === targetId || !activeServerId || !server) {
      resetChannelDrag()
      return
    }
    const targetChannel = server.channels.find((c) => c.id === targetId)
    if (!targetChannel) {
      resetChannelDrag()
      return
    }
    if (targetChannel.categoryId !== draggingCategoryId) {
      // Cross-category move
      moveChannelToCategory(activeServerId, draggedId, targetChannel.categoryId, targetId, channelDragPosition || 'below')
    } else {
      // Same-category reorder
      const categoryChannels = server.channels.filter((c) => c.categoryId === targetChannel.categoryId)
      const ids = categoryChannels.map((c) => c.id).filter((id) => id !== draggedId)
      const targetIndex = ids.indexOf(targetId)
      const insertIndex = channelDragPosition === 'below' ? targetIndex + 1 : targetIndex
      ids.splice(insertIndex, 0, draggedId)
      reorderChannels(activeServerId, targetChannel.categoryId, ids)
    }
    resetChannelDrag()
  }

  const resetChannelDrag = () => {
    setChannelDragOverId(null)
    setChannelDragPosition(null)
    setIsDraggingChannel(null)
    setDraggingCategoryId(null)
  }

  if (!activeServerId || !server) {
    return (
      <Sidebar>
        <View flex={1} alignItems="center" justifyContent="center" padding={16}>
          <Text color="$color8" fontFamily="$fontBody" fontSize={13} textAlign="center">
            Select a server to view channels
          </Text>
        </View>
      </Sidebar>
    )
  }

  // Group channels by category
  const channelsByCategory: Record<string, typeof server.channels> = {}
  for (const category of server.categories) {
    channelsByCategory[category.id] = server.channels.filter(c => c.categoryId === category.id)
  }

  return (
    <Sidebar>
      <View position="relative">
        <ServerHeader onPress={() => setServerHeaderDropdownOpen(!serverHeaderDropdownOpen)}>
          <View flexDirection="row" alignItems="center" gap={8} flex={1}>
            <ServerName>{server.name}</ServerName>
            <TierBadge tier={server.tier} size="sm" />
          </View>
          <ChevronDown size={16} color="$sidebarMuted" />
        </ServerHeader>
        {serverHeaderDropdownOpen && (
          <View position="absolute" top={48} left={0} right={0} zIndex={1000}>
            <ServerHeaderDropdown
              serverId={activeServerId}
              onClose={() => setServerHeaderDropdownOpen(false)}
            />
          </View>
        )}
      </View>

      {server.tier !== 'free' && server.bannerUrl && (
        <View height={120} overflow="hidden" position="relative">
          <img
            src={server.bannerUrl}
            alt={`${server.name} banner`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <View
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height={40}
            // @ts-expect-error -- web gradient
            style={{ background: 'linear-gradient(transparent, var(--sidebar))' }}
          />
        </View>
      )}

      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        <View paddingVertical={8}>
          {server.categories.map((category) => {
            const channels = channelsByCategory[category.id] || []
            return (
              <CategoryGroup key={category.id} name={category.name}>
                {channels.map((channel) => (
                  <ChannelItem
                    key={channel.id}
                    channel={channel}
                    isActive={activeChannelId === channel.id}
                    onPress={() => setActiveChannel(channel.id)}
                    onJoinVoice={joinVoiceChannel}
                    serverId={activeServerId}
                    voiceParticipants={
                      channel.type === 'voice'
                        ? voiceChannelParticipants[channel.id]
                        : undefined
                    }
                    onChannelDragStart={handleChannelDragStart}
                    onChannelDragOver={handleChannelDragOver}
                    onChannelDrop={handleChannelDrop}
                    onChannelDragEnd={resetChannelDrag}
                    channelDragOverId={channelDragOverId}
                    channelDragPosition={channelDragPosition}
                    isDraggingChannel={isDraggingChannel}
                  />
                ))}
              </CategoryGroup>
            )
          })}
        </View>
      </ScrollView>

      <FuelSupportBanner server={server} />

      {/* User section at bottom */}
      <UserSection>
        <View
          cursor="pointer"
          onPress={() => openProfileModal('u1')}
        >
          <Avatar name="Nova" color={theme.accent?.val || '#3ECFCF'} status="online" size="md" />
        </View>
        <View flexDirection="row" alignItems="center" gap={6} flex={1}>
          <View
            width={8}
            height={8}
            borderRadius="$borderRadiusFull"
            backgroundColor="#3ECF7B"
            cursor="pointer"
            onPress={() => setStatusPickerOpen(!statusPickerOpen)}
          />
          <View
            flex={1}
            cursor="pointer"
            onPress={() => openProfileModal('u1')}
          >
            <Username>Nova</Username>
            <UserTag
              // @ts-expect-error -- web filter
              style={streamerMode ? { filter: 'blur(4px)' } : undefined}
            >
              #0001
            </UserTag>
          </View>
        </View>
        <View position="relative">
          <SettingsButton onPress={() => navigateToUserSettings()}>
            <Settings size={16} color="$sidebarMuted" />
          </SettingsButton>
        </View>
      </UserSection>

      {/* Status picker popover */}
      {statusPickerOpen && (
        <View position="absolute" bottom={60} left={12} zIndex={2000}>
          <StatusPicker onClose={() => setStatusPickerOpen(false)} />
        </View>
      )}
    </Sidebar>
  )
}
